import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Desktop callback page that handles redirecting to custom protocol (mapmap://)
 * with cross-platform support for macOS, Windows, and Linux.
 *
 * Flow:
 * 1. Desktop app encodes redirect_uri in the state parameter
 * 2. Auth server redirects here with ?code=...&state=... (state contains redirect_uri)
 * 3. This page extracts the code and redirect_uri from state
 * 4. Detects OS and redirects appropriately
 * 5. Redirects to the custom protocol: mapmap://auth/callback?code=...&state=...
 *
 * Cross-Platform Support:
 * - macOS: Direct redirect works reliably
 * - Windows: Uses fallback methods if direct redirect fails
 * - Linux: Direct redirect with fallback instructions
 */

// =============================================================================
// Types
// =============================================================================

type OSPlatform = "macos" | "windows" | "linux" | "unknown";

// =============================================================================
// OS Detection Utility
// =============================================================================

/**
 * Detects the user's operating system from the browser's user agent
 */
function detectOS(): OSPlatform {
  const userAgent = navigator.userAgent.toLowerCase();

  // Windows detection (must check before mac because "windows" contains "win")
  if (userAgent.includes("win")) {
    return "windows";
  }
  // macOS detection
  if (userAgent.includes("mac")) {
    return "macos";
  }
  // Linux detection
  if (userAgent.includes("linux") && !userAgent.includes("android")) {
    return "linux";
  }

  return "unknown";
}

/**
 * Get human-readable OS name for display
 */
function getOSDisplayName(os: OSPlatform): string {
  switch (os) {
    case "macos":
      return "macOS";
    case "windows":
      return "Windows";
    case "linux":
      return "Linux";
    default:
      return "your system";
  }
}

// =============================================================================
// Main Component
// =============================================================================

export default function DesktopCallback() {
  const [searchParams] = useSearchParams();
  const redirectAttemptedRef = useRef(false);
  const [os, setOs] = useState<OSPlatform>("unknown");

  const code = searchParams.get("code");
  const state = searchParams.get("state");

  // Detect OS on mount
  useEffect(() => {
    const detectedOS = detectOS();
    setOs(detectedOS);
    console.log("🔐 Desktop callback: Detected OS:", detectedOS);
  }, []);

  console.log("🔐 Desktop callback: Component mounted");
  console.log("🔐 Desktop callback: Code:", code);
  console.log("🔐 Desktop callback: State:", state);
  console.log(
    "🔐 Desktop callback: All params:",
    Object.fromEntries(searchParams.entries())
  );

  // Extract redirect_uri from state (encoded by the desktop app)
  let redirectUri = "mapmap://auth/callback";
  if (state) {
    try {
      const stateData = JSON.parse(atob(state));
      console.log("🔐 Desktop callback: Parsed state data:", stateData);
      if (stateData.redirectUri) {
        redirectUri = stateData.redirectUri;
        console.log(
          "🔐 Desktop callback: Extracted redirect URI from state:",
          redirectUri
        );
      }
    } catch (e) {
      console.warn(
        "🔐 Desktop callback: Could not parse state, using default redirect URI",
        e
      );
    }
  }

  // Fallback: also check query param (for backwards compatibility)
  if (!redirectUri || redirectUri === "mapmap://auth/callback") {
    const queryRedirectUri = searchParams.get("redirect_uri");
    if (queryRedirectUri) {
      redirectUri = queryRedirectUri;
      console.log(
        "🔐 Desktop callback: Using redirect URI from query param:",
        redirectUri
      );
    }
  }

  // Construct the final redirect URL
  const finalUrl = code
    ? `${redirectUri}?code=${encodeURIComponent(code)}${
        state ? `&state=${encodeURIComponent(state)}` : ""
      }`
    : null;

  /**
   * Simple redirect handler - works for macOS and Linux
   */
  const handleRedirect = useCallback(() => {
    if (!code || !finalUrl) {
      console.error("❌ Desktop callback: No code received");
      return;
    }

    // Check if redirect was already attempted
    if (redirectAttemptedRef.current) {
      return;
    }

    // Mark as attempted immediately
    redirectAttemptedRef.current = true;
    console.log("🔐 Desktop callback: Redirecting to:", finalUrl);

    // Simple redirect - works reliably on macOS and Linux
    try {
      window.location.href = finalUrl;
    } catch (error) {
      console.error("❌ Desktop callback: Failed to redirect:", error);
      redirectAttemptedRef.current = false;
    }
  }, [code, finalUrl]);

  /**
   * Windows-specific fallback using iframe method
   */
  const handleWindowsFallback = useCallback(() => {
    if (!finalUrl) return;

    console.log("🔐 Desktop callback: Trying Windows iframe fallback");
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = finalUrl;
    document.body.appendChild(iframe);

    // Clean up iframe after attempt
    setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 3000);
  }, [finalUrl]);

  // Auto-redirect on mount
  useEffect(() => {
    if (!code) {
      console.error("❌ Desktop callback: No code received");
      return;
    }

    if (os === "unknown") {
      // Wait for OS detection
      return;
    }

    // Only attempt redirect once
    if (redirectAttemptedRef.current) {
      return;
    }

    // Delay redirect slightly to ensure page renders
    const timer = setTimeout(() => {
      handleRedirect();
    }, 500);

    return () => clearTimeout(timer);
  }, [code, os, handleRedirect]);

  // =============================================================================
  // Render
  // =============================================================================

  // The manual way out appears only if the app hasn't taken over by then:
  // most of the time the redirect works and the page never needs to say more.
  const [showHelp, setShowHelp] = useState(false);
  useEffect(() => {
    if (!code) return;
    const timer = setTimeout(() => setShowHelp(true), 2500);
    return () => clearTimeout(timer);
  }, [code]);

  const getPlatformHelpText = () => {
    switch (os) {
      case "windows":
        return (
          <>
            Your browser may ask to open MapMap. Choose <b>Open</b>. If nothing
            happens, make sure MapMap is installed and has been opened once.
          </>
        );
      case "macos":
        return (
          <>
            If your browser asks, choose <b>Open MapMap</b>.
          </>
        );
      case "linux":
        return (
          <>
            If nothing happens, your desktop may need to be set up to open{" "}
            <code>mapmap://</code> links.
          </>
        );
      default:
        return <>Make sure MapMap is installed, then try again.</>;
    }
  };

  return (
    <main className="mmcb">
      <style>{STAGE_CSS}</style>
      <div className="mmcb-block">
        <MapMapMark />
        <div className="mmcb-word" aria-label="MapMap">
          <span>Map</span>
          <span>Map</span>
        </div>

        {code && finalUrl ? (
          <>
            <h1 className="mmcb-title">Opening MapMap…</h1>
            <div className="mmcb-loader" aria-hidden="true">
              <div className="mmcb-thumb" />
            </div>
            <p className="mmcb-note">
              You&apos;re signed in. You can close this tab once MapMap opens.
            </p>

            <section
              className={`mmcb-help ${showHelp ? "mmcb-help-on" : ""}`}
              aria-hidden={!showHelp}
            >
              <p className="mmcb-help-lead">Didn&apos;t open?</p>
              <p className="mmcb-help-text">{getPlatformHelpText()}</p>
              <div className="mmcb-actions">
                <button
                  type="button"
                  className="mmcb-primary"
                  tabIndex={showHelp ? 0 : -1}
                  onClick={() => {
                    redirectAttemptedRef.current = false;
                    handleRedirect();
                  }}
                >
                  Open MapMap
                </button>
                {os === "windows" && (
                  <button
                    type="button"
                    className="mmcb-secondary"
                    tabIndex={showHelp ? 0 : -1}
                    onClick={handleWindowsFallback}
                  >
                    Try another way
                  </button>
                )}
              </div>
              {/* the raw link, for support: folded away, since it carries the code */}
              <details className="mmcb-details">
                <summary tabIndex={showHelp ? 0 : -1}>Show the link</summary>
                <code>{finalUrl}</code>
              </details>
            </section>
          </>
        ) : (
          <>
            <h1 className="mmcb-title">This sign-in link is incomplete</h1>
            <p className="mmcb-note">
              We didn&apos;t receive a sign-in code. Go back to MapMap and choose{" "}
              <b>Sign in to MapMap</b> again.
            </p>
          </>
        )}
      </div>
    </main>
  );
}

// =============================================================================
// Look
// =============================================================================

/**
 * The MapMap mark, inline so it needs no asset. Same paths as MapMap's own
 * public/splash.html (from mm-logo-mark-color.svg, filters dropped).
 */
function MapMapMark() {
  return (
    <svg
      className="mmcb-mark"
      viewBox="0 0 89 82"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="MapMap"
    >
      <path
        d="M21.7326 76.7156C21.9291 76.5191 22.1748 76.3471 22.2976 76.3471C22.7889 76.3471 23.4031 75.2171 23.6242 73.9397C23.747 73.1536 23.8453 69.3459 23.8698 65.4154C23.8698 61.5094 23.9681 56.498 24.0418 54.3362L24.0426 54.317C24.2137 50.3811 24.2149 50.3555 24.9016 49.6687C25.5403 49.0546 25.786 48.9563 27.1862 48.9563C28.6356 48.9563 28.8075 49.0055 29.4708 49.767C29.8884 50.1846 30.3306 50.5531 30.4535 50.5531C30.6008 50.5531 30.7237 50.7251 30.7237 50.9216C30.7237 51.1181 30.8956 51.2901 31.0922 51.2901C31.2887 51.2901 31.4606 51.462 31.4606 51.6586C31.4606 51.8551 31.5343 52.027 31.6572 52.027C31.8291 52.027 34.6542 54.6556 34.6542 54.8521C34.6542 54.9749 37.1599 57.4315 37.2827 57.4315C37.553 57.4315 38.5847 58.5615 38.5847 58.8563C38.5847 59.0037 38.7321 59.1511 38.9041 59.1511C39.0515 59.1511 39.42 59.495 39.6902 59.8881C39.9604 60.2811 40.2552 60.6251 40.3535 60.6251C40.4517 60.6251 41.0904 61.1655 41.7783 61.8288C43.2031 63.2045 44.3822 63.6221 45.7088 63.229C46.1755 63.1062 46.6669 62.836 46.7651 62.664C46.8634 62.4921 47.1336 62.3447 47.3056 62.3447C47.5021 62.3447 47.674 62.1727 47.674 61.9762C47.674 61.7797 47.7477 61.6077 47.8706 61.6077C48.0425 61.6077 49.148 60.5514 52.3661 57.2596C53.3242 56.2769 54.2085 55.4663 54.2822 55.4663C54.6016 55.4663 56.051 53.2799 56.2721 52.4692C56.4686 51.8551 56.4195 51.6094 55.9527 51.1181C54.9946 50.0618 54.184 49.3248 54.012 49.3248C53.8892 49.3248 53.8155 49.1529 53.8155 48.9563C53.8155 48.7352 53.6435 48.5141 53.4224 48.4159C53.0785 48.2931 48.1899 43.4536 45.2666 40.3829C44.677 39.7687 44.1611 39.2528 44.112 39.2528C44.0629 39.2528 40.7711 36.0347 36.816 32.1042C32.8609 28.1983 29.52 24.9064 29.4217 24.8082C29.3234 24.7099 28.6356 23.9975 27.9232 23.236C27.1862 22.4499 26.4738 21.8112 26.3264 21.8112C26.179 21.8112 26.0562 21.7129 26.0562 21.6146C26.0562 21.2707 25.1718 20.3372 24.8525 20.3372C24.7051 20.3372 24.5822 20.239 24.5822 20.0916C24.5822 19.7968 23.4031 18.6176 23.1083 18.6176C22.9609 18.6176 22.8626 18.4702 22.8626 18.3228C22.8626 18.1509 22.5187 17.7824 22.1257 17.5122C21.7326 17.2419 21.3887 16.898 21.3887 16.7752C21.3887 16.6278 21.143 16.3576 20.8728 16.161C20.578 15.9645 20.2095 15.6452 20.0621 15.4732C19.9147 15.3012 19.5954 14.9573 19.3252 14.7362C19.0795 14.5151 18.7356 14.1712 18.5882 13.9993C17.9741 13.2623 16.4264 11.7392 16.3281 11.7392C16.279 11.7392 15.0899 10.2985 13.444 8.65255C12.067 6.75256 11.0693 5.937 10.799 5.70189C10.5286 5.46678 9.64626 5.10645 8.34427 5.10645C7.58274 5.10645 6.69417 5.5197 6.3881 5.70189C6.3881 5.70189 5.0528 6.64168 5.05246 7.34193C5.05211 8.04219 4.97876 40.6777 4.97876 40.6777C4.92963 59.0037 4.97876 74.4801 5.05246 75.0697C5.12615 75.6347 5.32268 76.1015 5.47008 76.1015C5.59291 76.1015 5.98596 76.3226 6.32988 76.5928C6.91946 77.0595 7.33708 77.0841 14.1664 77.0841C20.2341 77.0841 21.4133 77.035 21.7326 76.7156Z"
        fill="#15C157"
      />
      <path
        d="M83.4653 76.4706C84.8655 75.2668 84.8164 76.5443 84.6936 41.1696C84.5953 7.83385 84.5509 6.8267 84.5509 6.70387C84.5509 6.58104 83.984 5.49335 83.4455 5.35267C82.7083 4.98426 82.0405 5.10704 81.0824 5.10704C79.7313 5.10704 79.0189 5.62292 79.0189 5.62292L54.8264 30.6555C54.6927 30.7892 54.259 31.2687 54.0109 31.903C53.8031 32.4345 53.7646 33.1287 53.7646 33.2967C53.7646 33.6652 53.8881 34.3894 55.4603 35.8879C56.8606 37.2391 60.2998 40.7028 62.1668 42.6435C62.7809 43.2822 63.4687 44.2403 63.7144 44.7807C63.96 45.3212 64.3285 46.0827 64.5251 46.4512C64.7216 46.8443 64.869 47.3847 64.869 47.6304C64.869 47.9006 65.0164 48.3673 65.1883 48.6621C65.4094 49.0798 65.5077 52.3716 65.606 61.8539C65.7288 73.7683 65.7534 74.5544 66.1955 75.2668C66.4658 75.6845 67.0062 76.2495 67.4238 76.5443C68.1854 77.0847 68.2836 77.1093 75.5551 77.0356C82.4826 76.9619 82.9494 76.9373 83.4653 76.4706Z"
        fill="#FBF7EF"
      />
    </svg>
  );
}

/*
  MapMap's startup stage (MapMap: components/startupStage, public/splash.html):
  the same ground, glow, mark and loading line, so the browser step looks like
  the app the user came from and is going back to. Brand green #15C157.
*/
const STAGE_CSS = `
@import url("https://fonts.googleapis.com/css2?family=Rubik:wght@600&display=swap");
.mmcb {
  position: fixed; inset: 0; overflow-y: auto;
  display: flex; align-items: center; justify-content: center;
  padding: 48px 24px;
  background:
    radial-gradient(1100px 640px at 50% 30%, rgba(21, 193, 87, 0.12), transparent 62%),
    radial-gradient(1700px 1100px at 50% 55%, #14161a 0%, #0d0e10 72%);
  color: #f4f5f4;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Inter, system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
}
.mmcb-block { width: 100%; max-width: 420px; margin: auto 0; text-align: center; }
.mmcb-mark {
  display: block; width: 72px; height: auto; margin: 0 auto 20px;
  filter: drop-shadow(0 0 28px rgba(34, 197, 94, 0.3));
}
.mmcb-word {
  font-family: Rubik, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  font-size: 26px; font-weight: 600; letter-spacing: -0.01em;
}
.mmcb-word span:first-child { color: #15c157; }
.mmcb-title {
  margin: 40px 0 0; font-size: 28px; font-weight: 650;
  letter-spacing: -0.02em; line-height: 1.2;
}
.mmcb-loader {
  position: relative; width: 180px; height: 3px; margin: 26px auto 0;
  border-radius: 999px; background: rgba(255, 255, 255, 0.08); overflow: hidden;
}
.mmcb-thumb {
  position: absolute; inset: 0 auto 0 0; width: 36%; border-radius: inherit;
  background: linear-gradient(90deg, transparent, #15c157 50%, transparent);
  animation: mmcb-slide 1.6s ease-in-out infinite;
}
.mmcb-note {
  margin: 22px auto 0; max-width: 340px;
  font-size: 15px; line-height: 1.55; color: #b6bcc1;
}
.mmcb-note b, .mmcb-help-text b { color: #f4f5f4; font-weight: 600; }
.mmcb-help {
  margin-top: 40px; padding-top: 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  opacity: 0; visibility: hidden; transform: translateY(6px);
  transition: opacity 0.4s ease, transform 0.4s ease, visibility 0s linear 0.4s;
}
.mmcb-help-on {
  opacity: 1; visibility: visible; transform: none;
  transition: opacity 0.4s ease, transform 0.4s ease, visibility 0s;
}
.mmcb-help-lead { margin: 0; font-size: 15px; font-weight: 600; }
.mmcb-help-text {
  margin: 8px auto 0; max-width: 360px;
  font-size: 14px; line-height: 1.55; color: #9aa1a6;
}
.mmcb-help-text code, .mmcb-details code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12.5px;
}
.mmcb-actions {
  margin-top: 22px; display: flex; flex-direction: column; align-items: center; gap: 10px;
}
.mmcb-primary, .mmcb-secondary { font: inherit; cursor: pointer; }
.mmcb-primary {
  min-width: 220px; height: 46px; padding: 0 26px; border: none; border-radius: 13px;
  background: linear-gradient(180deg, #2bd36c, #15c157);
  color: #04160b; font-size: 15px; font-weight: 650;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.38),
    inset 0 -1px 0 rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(0, 0, 0, 0.35),
    0 10px 28px -8px rgba(21, 193, 87, 0.55);
  transition: transform 0.15s ease, filter 0.15s ease;
}
.mmcb-primary:hover { transform: translateY(-1px); filter: brightness(1.06); }
.mmcb-primary:active { transform: none; filter: brightness(0.95); }
.mmcb-secondary {
  min-width: 220px; height: 42px; padding: 0 22px; border-radius: 13px;
  border: 1px solid rgba(255, 255, 255, 0.14); background: rgba(255, 255, 255, 0.03);
  color: #d9dde0; font-size: 14px; font-weight: 550;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.mmcb-secondary:hover { border-color: rgba(255, 255, 255, 0.26); background: rgba(255, 255, 255, 0.07); }
.mmcb-primary:focus-visible, .mmcb-secondary:focus-visible, .mmcb-details summary:focus-visible {
  outline: 2px solid #15c157; outline-offset: 3px;
}
.mmcb-details { margin-top: 22px; font-size: 13px; color: #7d8388; }
.mmcb-details summary { cursor: pointer; display: inline-block; border-radius: 4px; }
.mmcb-details summary:hover { color: #b6bcc1; }
.mmcb-details code {
  display: block; margin-top: 10px; padding: 10px 12px; border-radius: 10px;
  background: rgba(0, 0, 0, 0.3); border: 1px solid rgba(255, 255, 255, 0.06);
  color: #9aa1a6; text-align: left; word-break: break-all; user-select: all;
}
@keyframes mmcb-slide {
  from { transform: translateX(-110%); }
  to { transform: translateX(390%); }
}
@media (prefers-reduced-motion: reduce) {
  .mmcb-thumb { animation: none; transform: translateX(80%); opacity: 0.75; }
  .mmcb-help, .mmcb-help-on { transition: none; }
}
`;
